import { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Group,
  Stack,
  Alert,
  LoadingOverlay,
  Paper,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconAlertCircle, IconFlask, IconFileText } from '@tabler/icons-react';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { Practitioner } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import {
  ConsentSection,
  ConsentBanner,
  ConsentDisplay,
  SpecimenBox,
  PaymentSection,
  OrderSubStateStepper,
} from '../components';
import { OrderFormData } from '../components/OrderForm';
import { useOrderDetails } from '../hooks/useOrderDetails';
import { OrderBox } from '../components/OrderBox';
import { ReportBox } from '../components/ReportBox';
import { OrderInfoBanner } from './OrderInfoBanner';
import classes from './OrderPage.module.css';
import { OrderSubState } from '../lims-types';
import {
  getAllOrderSubStates,
  getOrderSubState,
  updateOrderSubState,
} from '../utils/order-substates-helper';
import { usePaymentTracker } from '../hooks/usePaymentTracker';

// -----------------------------------------------------------------------------
export const OrderPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const medplum = useMedplum();
  const currentUser = useMedplumProfile() as Practitioner;
  const { t } = useTranslation(['lims', 'common']);

  const [
    showConsentModal,
    { open: openConsentModal, close: closeConsentModal },
  ] = useDisclosure(false);
  const [subStateLoading, setSubStateLoading] = useState(false);

  const orderDetails = useOrderDetails(id);
  const {
    serviceRequest,
    getFormDataFromOrder,
    loading: orderLoading,
    loadOrderDetails,
    updateServiceRequest,
    specimens,
    patient,
    requester,
    consent,
    diagnosticReports,
  } = orderDetails;

  const hasConsent = consent !== undefined;
  const latestReport = diagnosticReports[0];

  const paymentTracker = usePaymentTracker(
    serviceRequest ?? undefined,
    patient
  );
  const { depositItems } = paymentTracker;

  // Invalidate and refetch reports when navigating back from report editor
  useEffect(() => {
    const state = location.state as { reloadReports?: boolean } | null;
    if (state?.reloadReports) {
      medplum.invalidateSearches('DiagnosticReport');
      loadOrderDetails();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate, medplum, loadOrderDetails]);

  const orderFormData = useMemo<OrderFormData>(() => {
    const initial = getFormDataFromOrder();
    return {
      patient: patient ?? undefined,
      requester: requester ?? undefined,
      testCode: '',
      testDisplay: '',
      sampleType: undefined,
      clinicalNotes: '',
      answerDelivery: [],
      authorizedPerson: '',
      authorizationFormFile: undefined,
      orderDate: new Date(),
      orderFile: undefined,
      referralReason: '',
      symptomOnsetAge: '',
      firstSymptom: '',
      ...initial,
    };
  }, [getFormDataFromOrder, patient, requester]);

  // ---------------------------------------------------------------------------
  const handleStartLabProcess = async () => {
    if (!serviceRequest?.id || !currentUser?.id) {
      return;
    }
    setSubStateLoading(true);
    try {
      const updatedSR = updateOrderSubState(
        {
          ...serviceRequest,
          performer: [{ reference: `Practitioner/${currentUser.id}` }],
        },
        OrderSubState.LabProcess
      );
      await medplum.updateResource(updatedSR);
      await loadOrderDetails();
    } finally {
      setSubStateLoading(false);
    }
  };

  const handleStartReport = async () => {
    if (!serviceRequest?.id) {
      return;
    }
    setSubStateLoading(true);
    try {
      await medplum.updateResource(
        updateOrderSubState({ ...serviceRequest }, OrderSubState.Report)
      );
      await loadOrderDetails();
    } finally {
      setSubStateLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  if (orderLoading) {
    return (
      <div className="relative h-96">
        <LoadingOverlay visible />
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  if (!serviceRequest) {
    return (
      <Stack gap="md" className="p-6">
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          {t('orderDetails.orderNotFound', { ns: 'lims' })}
        </Alert>
        <Button onClick={() => navigate('/lims')}>
          {t('orderDetails.backToOrders', { ns: 'lims' })}
        </Button>
      </Stack>
    );
  }

  // ---------------------------------------------------------------------------
  const handleConsentUpdate = async () => {
    await loadOrderDetails();
  };

  // ---------------------------------------------------------------------------
  return (
    <Box className={classes.pageWrapper}>
      {/* Sticky Banner */}
      <Box className={classes.stickyBanner}>
        <OrderInfoBanner
          serviceRequest={serviceRequest}
          patient={patient ?? undefined}
          requester={requester ?? undefined}
          formData={orderFormData}
        />
      </Box>

      {/* Scrollable Content */}
      <Box className={classes.scrollableContent}>
        <Group wrap="nowrap" align="flex-start" gap="md" p="md">
          <OrderSubStateStepper
            serviceRequest={serviceRequest}
            consent={consent}
            hasPayment={depositItems.length > 0}
            consentDate={consent?.dateTime}
            specimenDate={specimens[0]?.collection?.collectedDateTime}
            paymentDate={
              depositItems.length > 0 ? depositItems?.[0].meta?.lastUpdated : ''
            }
            latestReport={latestReport}
          />
          <Stack gap="md" flex={1}>
            {/* Consent banner when consent is missing */}
            {!hasConsent && (
              <ConsentBanner onCreateConsent={openConsentModal} />
            )}

            <Group align="flex-start" gap="xl" grow>
              <Stack>
                <OrderBox
                  patient={patient}
                  requester={requester}
                  formData={orderFormData}
                  serviceRequest={serviceRequest}
                  initialFormData={getFormDataFromOrder()}
                  onOrderUpdated={loadOrderDetails}
                />
                <SpecimenBox
                  specimens={specimens}
                  patient={patient}
                  serviceRequest={serviceRequest}
                  orderFormData={orderFormData}
                  onViewSpecimen={(specimen) =>
                    navigate(`/lims/specimen/${specimen.id}`)
                  }
                  onAdd={loadOrderDetails}
                  showAdd={serviceRequest.status === 'active'}
                />
                <PaymentSection
                  serviceRequest={serviceRequest}
                  paymentTracker={paymentTracker}
                />

                {/* Show consent display when consent exists */}
                {hasConsent && (
                  <ConsentDisplay
                    key={`consent-${serviceRequest.id}-${consent.id}`}
                    serviceRequest={serviceRequest}
                    consent={consent}
                    onEdit={openConsentModal}
                  />
                )}

                {/* Consent Modal */}
                {showConsentModal && (
                  <ConsentSection
                    serviceRequest={serviceRequest}
                    patient={orderDetails.patient}
                    editMode={true}
                    onConsentUpdate={handleConsentUpdate}
                    showModal={showConsentModal}
                    onCloseModal={closeConsentModal}
                  />
                )}
              </Stack>

              {(() => {
                const subStateIndex = getAllOrderSubStates().indexOf(
                  getOrderSubState(serviceRequest) ?? OrderSubState.OrderCreated
                );
                const reportStartedIndex = getAllOrderSubStates().indexOf(
                  OrderSubState.Report
                );
                return subStateIndex >= reportStartedIndex;
              })() && (
                <ReportBox
                  serviceRequest={serviceRequest}
                  onServiceRequestUpdate={updateServiceRequest}
                  latestReport={latestReport}
                />
              )}
            </Group>

            {/* Workflow action buttons */}
            {(() => {
              const currentSubState = getOrderSubState(serviceRequest);
              if (currentSubState === OrderSubState.OrderCreated) {
                return (
                  <Paper p="md" withBorder>
                    <Group>
                      <Button
                        leftSection={<IconFlask size={16} />}
                        loading={subStateLoading}
                        onClick={handleStartLabProcess}
                      >
                        {t('orderDetails.startLabProcess', {
                          ns: 'lims',
                          defaultValue: 'Start Lab Process',
                        })}
                      </Button>
                    </Group>
                  </Paper>
                );
              }
              if (currentSubState === OrderSubState.LabProcess) {
                return (
                  <Paper p="md" withBorder>
                    <Group>
                      <Button
                        leftSection={<IconFileText size={16} />}
                        loading={subStateLoading}
                        onClick={handleStartReport}
                      >
                        {t('orderDetails.startReport', {
                          ns: 'lims',
                          defaultValue: 'Start Report',
                        })}
                      </Button>
                    </Group>
                  </Paper>
                );
              }
              return null;
            })()}

            <Group justify="flex-start">
              <Button variant="subtle" onClick={() => navigate('/lims')}>
                {`← ${t('orderDetails.backToOrders', { ns: 'lims' })}`}
              </Button>
            </Group>
          </Stack>
        </Group>
      </Box>
    </Box>
  );
};
